export const MEASUREMENT_MODES = {
  ANGLE: "angle",
  LENGTH: "length",
  CONSTRUCT: "construct",
};

function isComprehensionStem(question) {
  return (
    question?.type === "grouped" &&
    (question?.passage_type === "comprehension" || (question?.question_text?.length ?? 0) > 400)
  );
}

function combinedQuestionText(question) {
  const subs = (question?.sub_questions || [])
    .map((s) => String(s?.question_text || ""))
    .join(" ");
  if (isComprehensionStem(question)) {
    return subs.toLowerCase();
  }
  const main = String(question?.question_text || question?.group_title || "");
  return `${main} ${subs}`.toLowerCase();
}

export function getMeasurementMode(question) {
  if (!question) return null;

  const forced = String(
    question?.answer_data?.force_measurement_tool ||
    question?.answer_data?.interactive?.force_tool ||
    question?.answer?.solution_geometry?.interactive?.force_tool ||
    ""
  ).toLowerCase();
  if (forced === "none") return null;
  if (forced === MEASUREMENT_MODES.ANGLE || forced === MEASUREMENT_MODES.LENGTH || forced === MEASUREMENT_MODES.CONSTRUCT) {
    return forced;
  }

  const combined = combinedQuestionText(question);
  const visualType = String(
    question?.answer_data?.visual_type || question?.visual_type || ""
  ).toLowerCase();

  if (
    /\bdraw accurately\b/.test(combined) ||
    /\baccurately\b.*\b(draw|construct)\b/.test(combined) ||
    /\b(draw|construct)\b.*\baccurately\b/.test(combined) ||
    /\bconstruct (a |the )?(triangle|rectangle|square|quadrilateral|polygon|parallelogram|trapezium)\b/.test(combined) ||
    /\bdraw (a |the )?(triangle|rectangle|square|quadrilateral|polygon|parallelogram|trapezium)\b/.test(combined)
  ) {
    return MEASUREMENT_MODES.CONSTRUCT;
  }

  if (
    visualType === "angle_diagram" ||
    /\bprotractor\b/.test(combined) ||
    /\bmeasure the angle/.test(combined) ||
    /\bmeasure.*\bangle/.test(combined) ||
    /\baccurately measure the angles/.test(combined) ||
    /\b(reflex|obtuse|acute)\b.*\bangle\b/.test(combined) ||
    /\bangle\b.*\b(reflex|obtuse|acute)\b/.test(combined) ||
    /\bangle between\b.*\b(hands?|lines?|arms?)\b/.test(combined) ||
    /\bsize of (the )?angle\b/.test(combined)
  ) {
    return MEASUREMENT_MODES.ANGLE;
  }

  if (
    /\bmeasure the line\b/.test(combined) ||
    /\bmeasure.*\b(line|segment)\b/.test(combined) ||
    /\blength of (ab|line|side|segment)\b/.test(combined) ||
    /\bmeasure.*\blength of\b/.test(combined) ||
    /\bmeasure.*\bside\b/.test(combined) ||
    /\bmeasure (ab|the line ab)\b/.test(combined)
  ) {
    return MEASUREMENT_MODES.LENGTH;
  }

  if (
    /\bestimate how far\b/.test(combined) ||
    /\bdrawn to a scale\b/.test(combined) ||
    /\bscale of\b/.test(combined) ||
    /\b1\s*cm\s*:\s*\d+/.test(combined) ||
    /\b1\s*cm\s+to\s+\d+/.test(combined) ||
    (/\bmap of\b/.test(combined) && /\b(scale|metres?|meters?|km|kilomet)/.test(combined)) ||
    (/\bfollowing the path\b/.test(combined) && /\b(metres?|meters?|km)\b/.test(combined))
  ) {
    return MEASUREMENT_MODES.LENGTH;
  }

  if (
    /\bmark the point\b/.test(combined) ||
    /\b\d+(?:\.\d+)?\s*cm from\b/.test(combined) ||
    /\bfraction of (ab|line)\b/.test(combined) ||
    /\bcompass\b/.test(combined) ||
    /\bdraw (a |the )?circle\b/.test(combined) ||
    /\bdraw (a |the )?arc\b/.test(combined) ||
    /\bwith (a )?radius( of)?\b/.test(combined) ||
    /\bset square\b/.test(combined) ||
    /\bdraw a perpendicular\b/.test(combined) ||
    /\bbisect (the )?(line|angle)\b/.test(combined)
  ) {
    return MEASUREMENT_MODES.CONSTRUCT;
  }

  return null;
}

// Human-readable trigger phrases per tool, shown in the editor so an admin can see WHY "auto" resolved
// to a tool for a given question. Kept in sync with the regexes in getMeasurementMode above.
export const MEASUREMENT_AUTO_PHRASES = [
  { mode: MEASUREMENT_MODES.CONSTRUCT, test: (t) => /\bdraw accurately\b/.test(t), phrase: "draw accurately" },
  { mode: MEASUREMENT_MODES.CONSTRUCT, test: (t) => /\baccurately\b.*\b(draw|construct)\b/.test(t) || /\b(draw|construct)\b.*\baccurately\b/.test(t), phrase: "accurately draw / construct" },
  { mode: MEASUREMENT_MODES.CONSTRUCT, test: (t) => /\b(draw|construct) (a |the )?(triangle|rectangle|square|quadrilateral|polygon|parallelogram|trapezium)\b/.test(t), phrase: "draw / construct a shape" },
  { mode: MEASUREMENT_MODES.ANGLE, test: (t) => /\bprotractor\b/.test(t), phrase: "protractor" },
  { mode: MEASUREMENT_MODES.ANGLE, test: (t) => /\bmeasure the angle/.test(t) || /\bmeasure.*\bangle/.test(t) || /\baccurately measure the angles/.test(t), phrase: "measure the angle" },
  { mode: MEASUREMENT_MODES.ANGLE, test: (t) => /\b(reflex|obtuse|acute)\b.*\bangle\b/.test(t) || /\bangle\b.*\b(reflex|obtuse|acute)\b/.test(t), phrase: "reflex / obtuse / acute angle" },
  { mode: MEASUREMENT_MODES.ANGLE, test: (t) => /\bangle between\b.*\b(hands?|lines?|arms?)\b/.test(t), phrase: "angle between the hands / lines" },
  { mode: MEASUREMENT_MODES.ANGLE, test: (t) => /\bsize of (the )?angle\b/.test(t), phrase: "size of the angle" },
  { mode: MEASUREMENT_MODES.LENGTH, test: (t) => /\bmeasure the line\b/.test(t) || /\bmeasure.*\b(line|segment)\b/.test(t) || /\blength of (ab|line|side|segment)\b/.test(t) || /\bmeasure.*\blength of\b/.test(t) || /\bmeasure.*\bside\b/.test(t) || /\bmeasure (ab|the line ab)\b/.test(t), phrase: "measure the line / length" },
  { mode: MEASUREMENT_MODES.LENGTH, test: (t) => /\bscale of\b/.test(t) || /\bdrawn to a scale\b/.test(t) || /\b1\s*cm\s*:\s*\d+/.test(t) || /\bestimate how far\b/.test(t), phrase: "scale drawing" },
  { mode: MEASUREMENT_MODES.CONSTRUCT, test: (t) => /\bcompass\b/.test(t) || /\bdraw (a |the )?circle\b/.test(t) || /\bdraw (a |the )?arc\b/.test(t) || /\bwith (a )?radius( of)?\b/.test(t), phrase: "compass / circle / arc / radius" },
  { mode: MEASUREMENT_MODES.CONSTRUCT, test: (t) => /\bset square\b/.test(t) || /\bdraw a perpendicular\b/.test(t) || /\bbisect (the )?(line|angle)\b/.test(t), phrase: "set square / perpendicular / bisect" },
];

// Returns { mode, phrase } describing what auto-detection would pick for this question's text, or null.
// Used by the editor to explain the "Auto" measurement-tool setting. Mode aligns with getMeasurementMode.
export function describeMeasurementAutoDetect(question) {
  const mode = getMeasurementMode(question);
  if (!mode) return null;
  const combined = combinedQuestionText(question);
  const hit = MEASUREMENT_AUTO_PHRASES.find((p) => p.mode === mode && p.test(combined));
  return { mode, phrase: hit ? hit.phrase : null };
}

export function getMeasurementTolerance(question) {
  const fromData = question?.answer_data?.measurement_tolerance;
  return {
    angle: Number(fromData?.angle) || 2,
    length_cm: Number(fromData?.length_cm) || 0.2,
    length_pct: Number(fromData?.length_pct) || 0.05,
  };
}

export function measurementModeForSub(parentQuestion, subQuestion) {
  if (isComprehensionStem(parentQuestion)) {
    return getMeasurementMode(subQuestion);
  }
  const subMode = getMeasurementMode(subQuestion);
  const parentMode = getMeasurementMode(parentQuestion);
  if (parentMode === MEASUREMENT_MODES.CONSTRUCT) return MEASUREMENT_MODES.CONSTRUCT;
  if (subMode) return subMode;
  return parentMode;
}

export function getPxPerCm(...questions) {
  for (const q of questions) {
    const v = Number(q?.answer_data?.geometry_config?.pixels_per_cm);
    if (Number.isFinite(v) && v > 0) return v;
  }
  return undefined;
}
